/**
 * Centralized Candy Configuration for DckSlap Candy Shop
 * 
 * This file contains the official candy data, versions, and paytables.
 * DO NOT modify Radix network integration code - this is display-only data.
 * 
 * Data Structure:
 * - 4 Candies: CocoaPop, SlapTaffy, NutPop, ShaftBerry
 * - 6 Versions each: Street, Juiced, Cracked, Unhinged, GoldMember, MythDck
 * - Levels 0-7 currently defined, 8-12 coming soon
 */

// =============================================================================
// VERSION TIERS (replaces the old "rarity" concept)
// =============================================================================

export const CANDY_VERSIONS = {
    Street: {
        tier: 1,
        color: '#9ca3af',
        gradient: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 50%, #6b7280 100%)',
        label: 'Street',
        description: 'Common street-level candy'
    },
    Juiced: {
        tier: 2,
        color: '#3b82f6',
        gradient: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #60a5fa 100%)',
        label: 'Juiced',
        description: 'Enhanced with extra flavor'
    },
    Cracked: {
        tier: 3,
        color: '#10b981',
        gradient: 'linear-gradient(135deg, #047857 0%, #10b981 50%, #34d399 100%)',
        label: 'Cracked',
        description: 'Premium quality drops'
    },
    Unhinged: {
        tier: 4,
        color: '#a855f7',
        gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        label: 'Unhinged',
        description: 'Wild and unpredictable'
    },
    GoldMember: {
        tier: 5,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 50%, #fbbf24 100%)',
        label: 'GoldMember',
        description: 'Exclusive golden edition'
    },
    MythDck: {
        tier: 6,
        color: '#ef4444',
        gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #f87171 100%)',
        label: 'MythDck',
        description: 'Legendary mythical tier'
    },
};

// Ordered array for display purposes (lowest to highest tier)
export const VERSION_ORDER = ['Street', 'Juiced', 'Cracked', 'Unhinged', 'GoldMember', 'MythDck'];

// =============================================================================
// CANDY DEFINITIONS
// =============================================================================

export const CANDIES = {
    cocoapop: {
        id: 'cocoapop',
        name: 'CocoaPop',
        icon: '🍫',
        description: 'Rich chocolatey delight'
    },
    slaptaffy: {
        id: 'slaptaffy',
        name: 'SlapTaffy',
        icon: '🍬',
        description: 'Chewy and satisfying'
    },
    nutpop: {
        id: 'nutpop',
        name: 'NutPop',
        icon: '🥜',
        description: 'Crunchy nutty goodness'
    },
    shaftberry: {
        id: 'shaftberry',
        name: 'ShaftBerry',
        icon: '🍓',
        description: 'Sweet berry blast'
    },
};

// Ordered array for display purposes
export const CANDY_ORDER = ['cocoapop', 'slaptaffy', 'nutpop', 'shaftberry'];

// =============================================================================
// LEVEL CONFIGURATION
// =============================================================================

export const LEVEL_CONFIG = {
    // Current effective max level for display
    EFFECTIVE_MAX_LEVEL: 11,
    // Levels with complete paytable data
    DEFINED_LEVELS: 11, // 0-11 (all defined)
    // Absolute max level (Scrypto component u8 max)
    ABSOLUTE_MAX_LEVEL: 255,
};

// Level names (0-11)
export const LEVEL_NAMES = [
    'Tiny Little Micro PP',
    "Piers' Pocket Picker",
    'The Thumb Thumper',
    'Respectable Wiggle Stick',
    'Midrange Meat Wand',
    'Certified Slap Rod',
    'The Thunder Plunger',
    'Full-Size Face-Smacker',
    'The Mega Mauler',
    'The Absolute Anaconda',
    'Galactic Girthmaster',
    'Supreme Overlord of Slap',
];

export const getLevelName = (level) => {
    if (typeof level !== 'number') return 'Unknown Level';
    return LEVEL_NAMES[level] || `Level ${level}`;
};

// Level tier thresholds (adjusted for 0-11 scale)
export const getLevelTier = (level) => {
    if (level >= 11) return { color: '#f59e0b', tier: 5 };
    if (level >= 7) return { color: '#a855f7', tier: 4 };
    if (level >= 5) return { color: '#3b82f6', tier: 3 };
    if (level >= 3) return { color: '#10b981', tier: 2 };
    return { color: '#9ca3af', tier: 1 };
};

// Milestones for level progress display
export const LEVEL_MILESTONES = [0, 3, 5, 7, 11];

// =============================================================================
// PAYTABLE DATA
// =============================================================================

/**
 * Paytable structure: PAYTABLE[level][candyId][version] = chance (as decimal)
 * null = not available at this level
 * 
 * Version order: Street/Juiced/Cracked/Unhinged/GoldMember/MythDck
 * DryPull (no candy) = 50% at all levels
 * 
 * Data from official spreadsheet:
 * - Levels 0-2: CocoaPop + SlapTaffy only
 * - Levels 3-6: CocoaPop + SlapTaffy + NutPop
 * - Levels 7-11: All 4 candies (ShaftBerry unlocked)
 */
export const PAYTABLE = {
    // Level 0: 12.5/7.5/3.5/1/.45/.05 for CocoaPop & SlapTaffy
    0: {
        cocoapop: {
            Street: 0.125,      // 12.5%
            Juiced: 0.075,      // 7.5%
            Cracked: 0.035,     // 3.5%
            Unhinged: 0.01,     // 1%
            GoldMember: 0.0045, // 0.45%
            MythDck: 0.0005,    // 0.05%
        },
        slaptaffy: {
            Street: 0.125,
            Juiced: 0.075,
            Cracked: 0.035,
            Unhinged: 0.01,
            GoldMember: 0.0045,
            MythDck: 0.0005,
        },
        nutpop: null,
        shaftberry: null,
    },
    // Level 1: Same as level 0
    1: {
        cocoapop: {
            Street: 0.125,
            Juiced: 0.075,
            Cracked: 0.035,
            Unhinged: 0.01,
            GoldMember: 0.0045,
            MythDck: 0.0005,
        },
        slaptaffy: {
            Street: 0.125,
            Juiced: 0.075,
            Cracked: 0.035,
            Unhinged: 0.01,
            GoldMember: 0.0045,
            MythDck: 0.0005,
        },
        nutpop: null,
        shaftberry: null,
    },
    // Level 2: Same as level 0
    2: {
        cocoapop: {
            Street: 0.125,
            Juiced: 0.075,
            Cracked: 0.035,
            Unhinged: 0.01,
            GoldMember: 0.0045,
            MythDck: 0.0005,
        },
        slaptaffy: {
            Street: 0.125,
            Juiced: 0.075,
            Cracked: 0.035,
            Unhinged: 0.01,
            GoldMember: 0.0045,
            MythDck: 0.0005,
        },
        nutpop: null,
        shaftberry: null,
    },
    // Level 3: 8/5/2.5/.8/.3/.0555 - NutPop unlocked
    3: {
        cocoapop: {
            Street: 0.08,       // 8%
            Juiced: 0.05,       // 5%
            Cracked: 0.025,     // 2.5%
            Unhinged: 0.008,    // 0.8%
            GoldMember: 0.003,  // 0.3%
            MythDck: 0.000555,  // 0.0555%
        },
        slaptaffy: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        nutpop: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        shaftberry: null,
    },
    // Level 4: Same as level 3
    4: {
        cocoapop: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        slaptaffy: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        nutpop: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        shaftberry: null,
    },
    // Level 5: Same as level 3
    5: {
        cocoapop: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        slaptaffy: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        nutpop: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        shaftberry: null,
    },
    // Level 6: Same as level 3
    6: {
        cocoapop: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        slaptaffy: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        nutpop: {
            Street: 0.08,
            Juiced: 0.05,
            Cracked: 0.025,
            Unhinged: 0.008,
            GoldMember: 0.003,
            MythDck: 0.000555,
        },
        shaftberry: null,
    },
    // Level 7: 6.25/3.75/1.75/.5/.22/.03 - ShaftBerry unlocked
    7: {
        cocoapop: {
            Street: 0.0625,     // 6.25%
            Juiced: 0.0375,     // 3.75%
            Cracked: 0.0175,    // 1.75%
            Unhinged: 0.005,    // 0.5%
            GoldMember: 0.0022, // 0.22%
            MythDck: 0.0003,    // 0.03%
        },
        slaptaffy: {
            Street: 0.0625,
            Juiced: 0.0375,
            Cracked: 0.0175,
            Unhinged: 0.005,
            GoldMember: 0.0022,
            MythDck: 0.0003,
        },
        nutpop: {
            Street: 0.0625,
            Juiced: 0.0375,
            Cracked: 0.0175,
            Unhinged: 0.005,
            GoldMember: 0.0022,
            MythDck: 0.0003,
        },
        shaftberry: {
            Street: 0.0625,
            Juiced: 0.0375,
            Cracked: 0.0175,
            Unhinged: 0.005,
            GoldMember: 0.0022,
            MythDck: 0.0003,
        },
    },
    // Level 8: 6/3.85/1.85/.52/.245/.035
    8: {
        cocoapop: {
            Street: 0.06,       // 6%
            Juiced: 0.0385,     // 3.85%
            Cracked: 0.0185,    // 1.85%
            Unhinged: 0.0052,   // 0.52%
            GoldMember: 0.00245,// 0.245%
            MythDck: 0.00035,   // 0.035%
        },
        slaptaffy: {
            Street: 0.06,
            Juiced: 0.0385,
            Cracked: 0.0185,
            Unhinged: 0.0052,
            GoldMember: 0.00245,
            MythDck: 0.00035,
        },
        nutpop: {
            Street: 0.06,
            Juiced: 0.0385,
            Cracked: 0.0185,
            Unhinged: 0.0052,
            GoldMember: 0.00245,
            MythDck: 0.00035,
        },
        shaftberry: {
            Street: 0.06,
            Juiced: 0.0385,
            Cracked: 0.0185,
            Unhinged: 0.0052,
            GoldMember: 0.00245,
            MythDck: 0.00035,
        },
    },
    // Level 9: 6/3.80/1.87/.54/.253/.037
    9: {
        cocoapop: {
            Street: 0.06,       // 6%
            Juiced: 0.038,      // 3.80%
            Cracked: 0.0187,    // 1.87%
            Unhinged: 0.0054,   // 0.54%
            GoldMember: 0.00253,// 0.253%
            MythDck: 0.00037,   // 0.037%
        },
        slaptaffy: {
            Street: 0.06,
            Juiced: 0.038,
            Cracked: 0.0187,
            Unhinged: 0.0054,
            GoldMember: 0.00253,
            MythDck: 0.00037,
        },
        nutpop: {
            Street: 0.06,
            Juiced: 0.038,
            Cracked: 0.0187,
            Unhinged: 0.0054,
            GoldMember: 0.00253,
            MythDck: 0.00037,
        },
        shaftberry: {
            Street: 0.06,
            Juiced: 0.038,
            Cracked: 0.0187,
            Unhinged: 0.0054,
            GoldMember: 0.00253,
            MythDck: 0.00037,
        },
    },
    // Level 10: 5.5/4.1/2/.6/.261/.039
    10: {
        cocoapop: {
            Street: 0.055,      // 5.5%
            Juiced: 0.041,      // 4.1%
            Cracked: 0.02,      // 2%
            Unhinged: 0.006,    // 0.6%
            GoldMember: 0.00261,// 0.261%
            MythDck: 0.00039,   // 0.039%
        },
        slaptaffy: {
            Street: 0.055,
            Juiced: 0.041,
            Cracked: 0.02,
            Unhinged: 0.006,
            GoldMember: 0.00261,
            MythDck: 0.00039,
        },
        nutpop: {
            Street: 0.055,
            Juiced: 0.041,
            Cracked: 0.02,
            Unhinged: 0.006,
            GoldMember: 0.00261,
            MythDck: 0.00039,
        },
        shaftberry: {
            Street: 0.055,
            Juiced: 0.041,
            Cracked: 0.02,
            Unhinged: 0.006,
            GoldMember: 0.00261,
            MythDck: 0.00039,
        },
    },
    // Level 11: 5/4.3/2.2/.675/.283/.042
    11: {
        cocoapop: {
            Street: 0.05,       // 5%
            Juiced: 0.043,      // 4.3%
            Cracked: 0.022,     // 2.2%
            Unhinged: 0.00675,  // 0.675%
            GoldMember: 0.00283,// 0.283%
            MythDck: 0.00042,   // 0.042%
        },
        slaptaffy: {
            Street: 0.05,
            Juiced: 0.043,
            Cracked: 0.022,
            Unhinged: 0.00675,
            GoldMember: 0.00283,
            MythDck: 0.00042,
        },
        nutpop: {
            Street: 0.05,
            Juiced: 0.043,
            Cracked: 0.022,
            Unhinged: 0.00675,
            GoldMember: 0.00283,
            MythDck: 0.00042,
        },
        shaftberry: {
            Street: 0.05,
            Juiced: 0.043,
            Cracked: 0.022,
            Unhinged: 0.00675,
            GoldMember: 0.00283,
            MythDck: 0.00042,
        },
    },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get formatted chance string (e.g., "12.50%" or "-")
 */
export const formatChance = (chance) => {
    if (chance === null || chance === undefined) return '-';
    const percent = chance * 100;
    const decimals = percent >= 1 ? 2 : 4;
    const formatted = percent.toFixed(decimals).replace(/\.?0+$/, '');
    return `${formatted}%`;
};

/**
 * Get version config by version name
 */
export const getVersionConfig = (version) => {
    return CANDY_VERSIONS[version] || CANDY_VERSIONS.Street;
};

/**
 * Get candy config by candy ID
 */
export const getCandyConfig = (candyId) => {
    return CANDIES[candyId] || null;
};

/**
 * Get candy ID by display name (case-insensitive)
 */
export const getCandyIdByName = (candyName) => {
    if (!candyName) return null;
    const normalized = String(candyName).toLowerCase();
    const match = Object.values(CANDIES).find(
        (candy) => candy.name.toLowerCase() === normalized
    );
    return match ? match.id : null;
};

/**
 * Check if a candy is available at a specific level
 */
export const isCandyAvailableAtLevel = (candyId, level) => {
    const levelData = PAYTABLE[level];
    if (!levelData) return false;
    return levelData[candyId] !== null;
};

/**
 * Get all available candies at a specific level
 */
export const getAvailableCandiesAtLevel = (level) => {
    const levelData = PAYTABLE[level];
    if (!levelData) return [];
    return CANDY_ORDER.filter(candyId => levelData[candyId] !== null);
};

/**
 * Get paytable entries for display in BenefitsList
 * Returns an array of { candy, candyName, version, chance, versionConfig }
 */
export const getPaytableForLevel = (level) => {
    const levelData = PAYTABLE[level];
    if (!levelData) {
        return { available: false, message: 'Coming Soon' };
    }

    const entries = [];

    CANDY_ORDER.forEach(candyId => {
        const candyData = levelData[candyId];
        const candyConfig = CANDIES[candyId];

        if (candyData === null) {
            // Candy not available at this level - add single entry showing unavailable
            entries.push({
                candy: candyId,
                candyName: candyConfig.name,
                candyIcon: candyConfig.icon,
                version: null,
                chance: null,
                versionConfig: null,
                available: false,
            });
        } else {
            // Add entry for each version
            VERSION_ORDER.forEach(version => {
                const chance = candyData[version];
                entries.push({
                    candy: candyId,
                    candyName: candyConfig.name,
                    candyIcon: candyConfig.icon,
                    version: version,
                    chance: chance,
                    versionConfig: CANDY_VERSIONS[version],
                    available: true,
                });
            });
        }
    });

    return { available: true, entries };
};

/**
 * Get level unlock summary for display
 */
export const getLevelUnlocks = () => [
    { level: 0, unlocks: ['CocoaPop', 'SlapTaffy'], description: '2 candies available' },
    { level: 3, unlocks: ['NutPop'], description: 'NutPop unlocked' },
    { level: 7, unlocks: ['ShaftBerry'], description: 'All 4 candies available!' },
    { level: 11, unlocks: [], description: 'Max level reached!' },
];

export default {
    CANDY_VERSIONS,
    VERSION_ORDER,
    CANDIES,
    CANDY_ORDER,
    LEVEL_CONFIG,
    LEVEL_NAMES,
    LEVEL_MILESTONES,
    PAYTABLE,
    getLevelTier,
    getLevelName,
    formatChance,
    getVersionConfig,
    getCandyConfig,
    getCandyIdByName,
    isCandyAvailableAtLevel,
    getAvailableCandiesAtLevel,
    getPaytableForLevel,
    getLevelUnlocks,
};
